<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import { formatFullDayDate } from '@/utils/dayjs/utils'

  const props = withDefaults(
    defineProps<{
      title: string
      description?: string
      isActive?: boolean
      disabled?: boolean
      price?: null | number
      showPopover?: boolean
      hidePrices?: boolean
    }>(),
    {
      description: '',
      isActive: false,
      disabled: false,
      price: null,
      showPopover: false,
      hidePrices: false
    }
  )

  const { locale } = useI18n()

  const tabBase = computed(() =>
    cva('flex flex-col px-4 py-2 h-14 w-full', {
      variants: {
        active: {
          true: 'tab--active border-border',
          false: 'tab--inactive border-none bg-grey'
        },
        disabled: {
          true: '',
          false: 'cursor-pointer'
        },
        priceSelected: {
          true: 'bg-light-info',
          false: ''
        },
        hasDescription: {
          true: 'text-left',
          false: ''
        }
      }
    })({
      priceSelected: !!props.price && !props.isActive,
      active: props.isActive,
      disabled: props.disabled,
      hasDescription: !!props.description
    })
  )

  const tabContainer = computed(() =>
    cva('flex w-full justify-center', {
      variants: {
        hasDescription: {
          true: 'md:justify-between',
          false: 'grow items-center'
        }
      }
    })({
      hasDescription: !!props.description
    })
  )

  const tabTitle = computed(() =>
    cva('text-subtitle-1', {
      variants: {
        isActive: {
          true: '',
          false: 'text-secondary '
        },
        hasDescription: {
          true: 'md:text-body-3',
          false: ''
        }
      }
    })({
      isActive: props.isActive,
      hasDescription: !!props.description
    })
  )

  const tabDescription = computed(() =>
    cva('', {
      variants: {
        hasDescription: {
          true: 'text-bt-blue-dark text-subtitle-1',
          false: 'text-secondary text-body-3'
        }
      }
    })({
      hasDescription: !!props.description
    })
  )

  watch(
    () => props.showPopover,
    (value) => {
      infoTooltip.value = value
    }
  )

  const infoTooltip = ref(props.showPopover)
  const { isDesktop } = useBTBreakpoints()
</script>

<template>
  <RadioGroupItem
    :class="tabBase"
    class="border-l border-r"
    :disabled="disabled"
    :aria-checked="isActive"
  >
    <div :class="tabContainer">
      <div>
        <div :class="tabTitle">{{ title }}</div>
        <div :class="tabDescription">
          {{ !!description ? formatFullDayDate(description, locale) : $t('fsf.selectDate') }}
        </div>
      </div>
      <div
        v-if="price && isDesktop && !hidePrices"
        class="flex items-center"
      >
        <UiPopover
          v-model="infoTooltip"
          show-arrow
          side="top"
          arrow-color="fill-light-warning"
          :arrow-height="10"
          :arrow-width="20"
          :offset="isDesktop ? 10 : 0"
          close-manually
        >
          <template #anchor>
            <FsfPrice
              class="font-medium"
              :price="price"
            />
          </template>
          <template #content>
            <div
              class="flex w-screen cursor-default justify-between rounded bg-light-warning py-3 pl-4 pr-2 text-center text-body-2 md:w-96"
            >
              <div class="max-w-full">
                {{ $t('fsf.priceUpdateDisclaimer') }}
              </div>
              <div>
                <button
                  class="text-bt-blue-dark/30"
                  @click="infoTooltip = false"
                >
                  <Icon name="common/close" />
                </button>
              </div>
            </div>
          </template>
        </UiPopover>
      </div>
    </div>
  </RadioGroupItem>
</template>

<style scoped lang="scss">
  .tab {
    &--active {
      box-shadow: inset 0 4px theme('colors.bt.green.DEFAULT');
    }
    &--inactive {
      box-shadow: inset 0px -1px 0px 0px theme('colors.border');
    }
  }
</style>
